import React from 'react';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import PropTypes from 'prop-types';
import { RenderQuote } from './RenderQuote';
import { RenderTable } from './RenderTable';
import { RenderParagraph } from './RenderParagraph';
import { RenderHyperlink } from './RenderHyperlink';
import { generateKey, generateNestedKey, getVariant } from './utils';

export const options = {
  preserveWhitespaces: true,
  renderMark: {
    [MARKS.BOLD]: (text) => (
      <b
        className="sui-font-bold"
        key={generateKey(text, 0, 'bold-text')}
      >
        {text}
      </b>
    ),
    [MARKS.ITALIC]: (text) => (
      <i key={generateKey(text, 0, 'italic-text')}>
        {text}
      </i>
    ),
    [MARKS.UNDERLINE]: (text) => (
      <u
        key={generateKey(text, 0, 'underline-text')}
        className="sui-underline"
      >
        {text}
      </u>
    ),
    [MARKS.SUPERSCRIPT]: (text) => (
      <sup key={generateKey(null, 0, 'sup-text')}>
        {text}
      </sup>
    ),
    [MARKS.SUBSCRIPT]: (text) => (
      <sub key={generateKey(null, 0, 'sub-text')}>
        {text}
      </sub>
    ),
    [MARKS.CODE]: (text) => (
      <mark
        key={generateKey(text, 0, 'code-text')}
        style={{ backgroundColor: '#E1E2E0' }}
      >
        {text}
      </mark>
    )
  },
  renderNode: {
    [BLOCKS.HR]: () => <hr key={generateKey(null, 0, 'hr')} />,
    [BLOCKS.UL_LIST]: (node, children) => (
      <ul
        key={generateNestedKey(node, 'ulist', 0)}
        className="sui-pl-4 sui-list-disc"
      >
        {children}
      </ul>
    ),
    [BLOCKS.OL_LIST]: (node, children) => (
      <ol
        key={generateNestedKey(node, 'olist', 0)}
        className="sui-pl-4 sui-list-decimal"
      >
        {children}
      </ol>
    ),
    [BLOCKS.LIST_ITEM]: (node, children) => (
      <li key={generateNestedKey(node, 'list', 0)}>
        {children}
      </li>
    ),
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <RenderParagraph
        key={generateNestedKey(node, getVariant(node?.nodeType), 0)}
        node={node}
      >
        {children}
      </RenderParagraph>
    ),
    [BLOCKS.HEADING_1]: (node, children) => (
      <RenderParagraph
        key={generateNestedKey(node, getVariant(node?.nodeType), 0)}
        node={node}
      >
        {children}
      </RenderParagraph>
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <RenderParagraph
        key={generateNestedKey(node, getVariant(node?.nodeType), 0)}
        node={node}
      >
        {children}
      </RenderParagraph>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <RenderParagraph
        key={generateNestedKey(node, getVariant(node?.nodeType), 0)}
        node={node}
      >
        {children}
      </RenderParagraph>
    ),
    [BLOCKS.HEADING_4]: (node, children) => (
      <RenderParagraph
        key={generateNestedKey(node, getVariant(node?.nodeType), 0)}
        node={node}
      >
        {children}
      </RenderParagraph>
    ),
    [BLOCKS.HEADING_5]: (node, children) => (
      <RenderParagraph
        key={generateNestedKey(node, getVariant(node?.nodeType), 0)}
        node={node}
      >
        {children}
      </RenderParagraph>
    ),
    [BLOCKS.HEADING_6]: (node, children) => (
      <RenderParagraph
        key={generateNestedKey(node, getVariant(node?.nodeType), 0)}
        node={node}
      >
        {children}
      </RenderParagraph>
    ),
    [BLOCKS.QUOTE]: (node, children) => (
      <RenderQuote
        key={generateNestedKey(node, 'quote', 0)}
        node={node}
      >
        {children}
      </RenderQuote>
    ),
    [INLINES.HYPERLINK]: (node, children) => (
      <RenderHyperlink
        key={generateNestedKey(node, 'hyperlink', 0)}
        node={node}
      >
        {children}
      </RenderHyperlink>
    ),
    [BLOCKS.TABLE]: (node) => (
      <RenderTable
        key={generateNestedKey(node, 'render-table', 0)}
        node={node}
      />
    )
  }
};

export const RichText = ({ content, isLivePreview }) => {
  if (content) {
    let descriptionContent;
    try {
      descriptionContent = isLivePreview ? content : JSON.parse(content);
    } catch (error) {
      return null;
    }
    return documentToReactComponents(descriptionContent, options);
  }
  return null;
};

RichText.propTypes = {
  content: PropTypes.string.isRequired,
  isLivePreview: PropTypes.bool,
};

RichText.defaultProps = {
  isLivePreview: false,
};
